import { UserRoundX } from '@/lib/icons';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters } from '@/types/reports';
import { formatDisplayDateTime } from '@/lib/date-utils';

interface WorkspaceEntitlement {
  id: string;
  attributes: {
    changed_at: string;
    action: string;
    workspace_type: string;
    first_name: string;
    last_name: string;
    user_email: string;
    workspace_name: string;
  };
}

const columns: ColumnDef<WorkspaceEntitlement>[] = [
  {
    key: 'date',
    header: 'Date',
    render: (item) => formatDisplayDateTime(item.attributes.changed_at),
  },
  {
    key: 'action',
    header: 'Action',
    render: (item) => {
      const action = item.attributes.action;
      const capitalizedAction = action ? action.charAt(0).toUpperCase() + action.slice(1) : '';
      return item.attributes.workspace_type === 'Product'
        ? `Entitlement ${capitalizedAction}`
        : `Customer ${capitalizedAction}`;
    },
  },
  {
    key: 'user',
    header: 'User',
    render: (item) => (
      <div>
        <div className="font-medium">
          {item.attributes.first_name} {item.attributes.last_name}
        </div>
        <div className="text-muted-foreground text-xs">
          {item.attributes.user_email}
        </div>
      </div>
    ),
  },
  {
    key: 'product',
    header: 'Product',
    render: (item) => item.attributes.workspace_name,
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'from_date', type: 'date', label: 'From Date', placeholder: 'Select date' },
    { key: 'to_date', type: 'date', label: 'To Date', placeholder: 'Select date' },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(filters.from_date || filters.to_date),
};

export function WorkspaceEntitlementsReport() {
  return (
    <ReportPageLayout<WorkspaceEntitlement>
      title="Workspace Entitlements Report"
      description="Track workspace access changes in your organization."
      breadcrumbLabel="Workspace Entitlements"
      itemLabel="entitlement changes"
      queryKey="workspace-entitlements"
      fetchData={reportsService.getWorkspaceEntitlements}
      downloadData={reportsService.downloadWorkspaceEntitlements}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort(
          (a, b) =>
            new Date(b.attributes.changed_at).getTime() -
            new Date(a.attributes.changed_at).getTime()
        )
      }
      getRowKey={(item) => item.id}
      emptyIcon={<UserRoundX />}
      emptyTitle="No workspace entitlement changes found"
      emptyDescription="Try adjusting your filters to find entitlement changes."
    />
  );
}
